import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { DateRangeFilter, ExportBar } from '../components/Filters';
import { ColFilter } from '../components/ColFilter';

/* ─── Constants ──────────────────────────────────────────────── */
const STATUS_COLORS = {
  'SUCCESS':                  '#44BBA4',
  'FAILED: INC-20A REQUIRED': '#E8C547',
  'FAILED':                   '#E84855',
  'SESSION_DEAD':             '#A23B72',
};

const CONTACT_CHANNELS = [
  { id: 'whatsapp', label: 'WhatsApp', icon: '💬', color: '#25D366' },
  { id: 'email',    label: 'Email',    icon: '✉️',  color: '#4285F4' },
  { id: 'call',     label: 'Phone Call', icon: '📞', color: '#FF6B35' },
];

// Data source filter groups for viewer
const DATA_SOURCE_FILTERS = [
  { id: '',        label: 'All Companies',    icon: '🏢', desc: 'Show all records' },
  { id: 'iec',     label: 'IEC Data',         icon: '🚢', desc: 'Has Import/Export Code' },
  { id: 'gst',     label: 'GST Data',         icon: '🧾', desc: 'Has GST registration' },
  { id: 'inc20a',  label: 'INC-20A Required', icon: '⚠️', desc: 'MCA filing pending' },
  { id: 'mca',     label: 'MCA Data',         icon: '📋', desc: 'Has MCA master data' },
];

// Intersection filters (companies matching multiple criteria)
const INTERSECTION_FILTERS = [
  { id: '',             label: 'No Intersection Filter', icon: '◯' },
  { id: 'iec_gst',      label: 'IEC + GST',       icon: '⚓', color: '#6C63FF' },
  { id: 'gst_mca',      label: 'GST + MCA Data',  icon: '📊', color: '#FF6B6B' },
  { id: 'iec_gst_mca',  label: 'IEC + GST + MCA', icon: '🎯', color: '#44BBA4' },
  { id: 'no_gst_no_iec',label: 'No GST & No IEC', icon: '❌', color: '#E8C547' },
  { id: 'contact_ready',label: 'Has Email or Phone', icon: '📲', color: '#FF9F43' },
];

/* ─── Helper Components ──────────────────────────────────────── */
function ContactChannelBadges({ via, called }) {
  const channels = via ? via.split(',') : [];
  if (called === 'YES' && !channels.includes('call')) channels.push('call');
  if (!channels.length) return null;
  return (
    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '3px' }}>
      {channels.map(ch => {
        const def = CONTACT_CHANNELS.find(c => c.id === ch);
        if (!def) return null;
        return (
          <span key={ch} style={{
            fontSize: '10px', fontWeight: 600,
            background: def.color + '18',
            color: def.color,
            border: `1px solid ${def.color}44`,
            borderRadius: '10px',
            padding: '1px 7px',
            display: 'inline-flex', alignItems: 'center', gap: '3px'
          }}>
            {def.icon} {def.label}
          </span>
        );
      })}
    </div>
  );
}

/* Viewer Progress Banner */
function ViewerProgressBanner({ total, contactedCount, limit }) {
  const remaining = Math.max(0, limit - contactedCount);
  const nextBatchUnlocked = contactedCount >= limit;
  const pct = Math.min(100, Math.round((contactedCount / limit) * 100));
  const ringColor = pct === 100 ? '#44BBA4' : pct > 60 ? '#E8C547' : '#4285F4';

  return (
    <div style={{
      background: 'linear-gradient(135deg, #0B2C4A 0%, #0F3A60 60%, #1A4A72 100%)',
      borderRadius: '14px',
      padding: '20px 24px',
      marginBottom: '18px',
      display: 'flex',
      alignItems: 'center',
      gap: '20px',
      flexWrap: 'wrap',
      boxShadow: '0 4px 24px rgba(11,44,74,0.18)',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Background decoration */}
      <div style={{
        position: 'absolute', right: '-20px', top: '-20px',
        width: '140px', height: '140px', borderRadius: '50%',
        background: 'rgba(255,255,255,0.03)',
        pointerEvents: 'none',
      }} />

      {/* Ring progress */}
      <div style={{ position: 'relative', width: '72px', height: '72px', flexShrink: 0 }}>
        <svg width="72" height="72" viewBox="0 0 72 72">
          <circle cx="36" cy="36" r="30" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="7" />
          <circle
            cx="36" cy="36" r="30"
            fill="none"
            stroke={ringColor}
            strokeWidth="7"
            strokeLinecap="round"
            strokeDasharray={`${2 * Math.PI * 30}`}
            strokeDashoffset={`${2 * Math.PI * 30 * (1 - pct / 100)}`}
            transform="rotate(-90 36 36)"
            style={{ transition: 'stroke-dashoffset 0.6s ease' }}
          />
        </svg>
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexDirection: 'column',
        }}>
          <span style={{ fontSize: '15px', fontWeight: 800, color: ringColor }}>{pct}%</span>
        </div>
      </div>

      {/* Stats */}
      <div style={{ flex: 1, minWidth: '180px' }}>
        <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.55)', marginBottom: '6px', letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 600 }}>
          Contact Progress
        </div>
        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '26px', fontWeight: 800, color: '#fff', lineHeight: 1 }}>{contactedCount}</div>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.45)', marginTop: '2px' }}>Contacted</div>
          </div>
          <div>
            <div style={{ fontSize: '26px', fontWeight: 800, color: 'rgba(255,255,255,0.4)', lineHeight: 1 }}>{limit}</div>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)', marginTop: '2px' }}>Batch Size</div>
          </div>
          <div>
            <div style={{ fontSize: '26px', fontWeight: 800, color: remaining > 0 ? '#E8C547' : '#44BBA4', lineHeight: 1 }}>{remaining}</div>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.45)', marginTop: '2px' }}>Remaining</div>
          </div>
          <div>
            <div style={{ fontSize: '26px', fontWeight: 800, color: '#44BBA4', lineHeight: 1 }}>{total.toLocaleString('en-IN')}</div>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.45)', marginTop: '2px' }}>Total in DB</div>
          </div>
        </div>
      </div>

      {/* Status pill */}
      <div style={{ flexShrink: 0 }}>
        {nextBatchUnlocked ? (
          <div style={{
            background: 'rgba(68,187,164,0.2)',
            border: '1px solid rgba(68,187,164,0.5)',
            borderRadius: '10px',
            padding: '10px 16px',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '20px', marginBottom: '3px' }}>🎉</div>
            <div style={{ fontSize: '12px', fontWeight: 700, color: '#44BBA4' }}>Batch Complete!</div>
            <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)', marginTop: '2px' }}>Next 100 unlocked</div>
          </div>
        ) : (
          <div style={{
            background: 'rgba(232,197,71,0.12)',
            border: '1px solid rgba(232,197,71,0.3)',
            borderRadius: '10px',
            padding: '10px 16px',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#E8C547', letterSpacing: '0.06em' }}>VIEWING BATCH</div>
            <div style={{ fontSize: '22px', fontWeight: 800, color: '#fff', lineHeight: 1.2 }}>
              {Math.min(limit, total).toLocaleString('en-IN')}
            </div>
            <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', marginTop: '2px' }}>
              Contact {remaining} more to unlock
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* Data Source Filter Tabs */
function DataSourceTabs({ value, onChange }) {
  return (
    <div style={{
      display: 'flex',
      gap: '6px',
      flexWrap: 'wrap',
      marginBottom: '10px',
    }}>
      {DATA_SOURCE_FILTERS.map(f => {
        const active = value === f.id;
        return (
          <button
            key={f.id}
            onClick={() => onChange(f.id)}
            title={f.desc}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 14px',
              borderRadius: '20px',
              border: `1.5px solid ${active ? 'var(--navy-3)' : 'var(--border)'}`,
              background: active
                ? 'linear-gradient(135deg, var(--navy) 0%, var(--navy-3) 100%)'
                : 'var(--surface)',
              color: active ? '#fff' : 'var(--text-2)',
              fontSize: '12.5px',
              fontWeight: active ? 700 : 500,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              boxShadow: active ? '0 2px 8px rgba(11,44,74,0.2)' : 'none',
              whiteSpace: 'nowrap',
            }}
          >
            <span style={{ fontSize: '13px' }}>{f.icon}</span>
            {f.label}
          </button>
        );
      })}
    </div>
  );
}

/* Intersection Filter Pills */
function IntersectionPills({ value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
      <span style={{
        fontSize: '11px', fontWeight: 700, letterSpacing: '0.07em',
        textTransform: 'uppercase', color: 'var(--text-3)',
        whiteSpace: 'nowrap', marginRight: '4px',
      }}>
        ∩ Intersection
      </span>
      {INTERSECTION_FILTERS.map(f => {
        const active = value === f.id;
        const color = f.color || 'var(--navy)';
        return (
          <button
            key={f.id}
            onClick={() => onChange(f.id === value ? '' : f.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              padding: '5px 11px',
              borderRadius: '16px',
              border: `1.5px solid ${active ? color : 'var(--border)'}`,
              background: active ? color + '15' : 'transparent',
              color: active ? color : 'var(--text-2)',
              fontSize: '11.5px',
              fontWeight: active ? 700 : 500,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              whiteSpace: 'nowrap',
            }}
          >
            <span style={{ fontSize: '12px' }}>{f.icon}</span>
            {f.label}
          </button>
        );
      })}
    </div>
  );
}

/* ─── Main Component ─────────────────────────────────────────── */
export default function Companies() {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'F12') { window.location.href = '/login'; e.preventDefault(); }
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'I' || e.key === 'i')) { window.location.href = '/login'; e.preventDefault(); }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'U' || e.key === 'u')) { window.location.href = '/login'; e.preventDefault(); }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const { can, user } = useAuth();
  const nav = useNavigate();
  const isViewer = user?.role === 'viewer';

  const getSaved = (key, defaultVal) => {
    try { const v = sessionStorage.getItem(`cmp_${key}`); return v !== null ? JSON.parse(v) : defaultVal; }
    catch { return defaultVal; }
  };

  const [records, setRecords]       = useState([]);
  const [total, setTotal]           = useState(0);
  const [contactedCount, setContactedCount] = useState(0); // viewer: how many they've contacted
  const [page, setPage]             = useState(() => getSaved('page', 1));
  const [search, setSearch]         = useState(() => getSaved('search', ''));
  const [dSearch, setDSearch]       = useState(() => getSaved('dSearch', ''));
  const [contactedFilter, setContactedFilter] = useState(() => getSaved('contactedFilter', ''));
  const [loading, setLoading]       = useState(true);
  const [dateFilters, setDateFilters] = useState(() => getSaved('dateFilters', { fromDate: '', toDate: '' }));
  const [hasMaster, setHasMaster]     = useState(() => getSaved('hasMaster', ''));
  const [mgt7aStatus, setMgt7aStatus] = useState(() => getSaved('mgt7aStatus', ''));
  const [hasGst, setHasGst]           = useState(() => getSaved('hasGst', ''));
  const [hasIec, setHasIec]           = useState(() => getSaved('hasIec', ''));
  const [contactInfo, setContactInfo] = useState(() => getSaved('contactInfo', ''));
  const [cityFilter, setCityFilter]   = useState('');
  const [stateFilter, setStateFilter] = useState('');

  // New filter states
  const [dataSource, setDataSource]         = useState(() => getSaved('dataSource', ''));
  const [intersectionFilter, setIntersectionFilter] = useState(() => getSaved('intersectionFilter', ''));

  const VIEWER_LIMIT = 100; // records per batch for viewers

  useEffect(() => {
    sessionStorage.setItem('cmp_page', JSON.stringify(page));
    sessionStorage.setItem('cmp_search', JSON.stringify(search));
    sessionStorage.setItem('cmp_dSearch', JSON.stringify(dSearch));
    sessionStorage.setItem('cmp_contactedFilter', JSON.stringify(contactedFilter));
    sessionStorage.setItem('cmp_dateFilters', JSON.stringify(dateFilters));
    sessionStorage.setItem('cmp_hasMaster', JSON.stringify(hasMaster));
    sessionStorage.setItem('cmp_mgt7aStatus', JSON.stringify(mgt7aStatus));
    sessionStorage.setItem('cmp_hasGst', JSON.stringify(hasGst));
    sessionStorage.setItem('cmp_hasIec', JSON.stringify(hasIec));
    sessionStorage.setItem('cmp_contactInfo', JSON.stringify(contactInfo));
    sessionStorage.setItem('cmp_dataSource', JSON.stringify(dataSource));
    sessionStorage.setItem('cmp_intersectionFilter', JSON.stringify(intersectionFilter));
  }, [page, search, dSearch, contactedFilter, dateFilters, hasMaster, mgt7aStatus, hasGst, hasIec, contactInfo, cityFilter, stateFilter, dataSource, intersectionFilter]);

  // Contact Modal State
  const [contactModal, setContactModal]     = useState(null);
  const [contactNote, setContactNote]       = useState('');
  const [contactVia, setContactVia]         = useState([]);
  const [hasCalled, setHasCalled]           = useState(false);
  const [contactSaving, setContactSaving]   = useState(false);

  // For viewers: limit is VIEWER_LIMIT, with unlock-next-batch logic
  const limit = isViewer ? VIEWER_LIMIT : 50;

  useEffect(() => {
    const t = setTimeout(() => {
      if (search !== dSearch) { setDSearch(search); setPage(1); }
    }, 400);
    return () => clearTimeout(t);
  }, [search, dSearch]);

  const buildParams = useCallback(() => {
    const p = { page, limit };
    if (dSearch) p.search = dSearch;
    if (contactedFilter) p.contacted = contactedFilter;
    if (dateFilters.fromDate) p.fromDate = dateFilters.fromDate;
    if (dateFilters.toDate)   p.toDate   = dateFilters.toDate;
    if (hasMaster) p.hasMaster = hasMaster;
    if (mgt7aStatus) p.mgt7aStatus = mgt7aStatus;
    if (hasGst) p.hasGst = hasGst;
    if (hasIec) p.hasIec = hasIec;
    if (contactInfo) p.contactInfo = contactInfo;
    if (cityFilter) p.city = cityFilter;
    if (stateFilter) p.state = stateFilter;

    // Data source filter (viewer & analyst)
    if (dataSource === 'iec')    { p.hasIec = 'YES'; }
    if (dataSource === 'gst')    { p.hasGst = 'YES'; }
    if (dataSource === 'inc20a') { p.mgt7aStatus = 'FAILED: INC-20A REQUIRED'; }
    if (dataSource === 'mca')    { p.hasMaster = 'YES'; }

    // Intersection filters
    if (intersectionFilter === 'iec_gst')      { p.hasIec = 'YES'; p.hasGst = 'YES'; }
    if (intersectionFilter === 'gst_mca')      { p.hasGst = 'YES'; p.hasMaster = 'YES'; }
    if (intersectionFilter === 'iec_gst_mca')  { p.hasIec = 'YES'; p.hasGst = 'YES'; p.hasMaster = 'YES'; }
    if (intersectionFilter === 'no_gst_no_iec'){ p.hasGst = 'NO'; p.hasIec = 'NO'; }
    if (intersectionFilter === 'contact_ready'){ p.contactInfo = 'either'; }

    // Viewer batch offset (unlock next 100 after all contacted)
    if (isViewer) {
      p.viewerBatch = Math.floor(contactedCount / VIEWER_LIMIT);
    }

    return p;
  }, [page, dSearch, contactedFilter, dateFilters, hasMaster, mgt7aStatus, hasGst, hasIec, contactInfo, cityFilter, stateFilter, dataSource, intersectionFilter, isViewer, contactedCount]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.companies.list(buildParams());
      setRecords(res.rows);
      setTotal(res.total);
      // If API returns contacted_count for viewer progress tracking
      if (res.contacted_count != null) setContactedCount(res.contacted_count);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleExport = (fmt) => {
    const p = buildParams();
    delete p.page; delete p.limit;
    p.format = fmt;
    api.companies.export(p);
  };

  const openContactModal = (row) => {
    const existingVia = row.CONTACTED_VIA ? row.CONTACTED_VIA.split(',') : [];
    setContactNote(row.NOTE || '');
    setContactVia(existingVia);
    setHasCalled(row.HAS_CALLED === 'YES' || existingVia.includes('call'));
    setContactModal({
      cin: row.CIN,
      name: row.COMPANY_NAME,
      contacted: row.CONTACTED === 'YES',
      note: row.NOTE,
      contactedBy: row.CONTACTED_BY,
      contactedAt: row.CONTACTED_AT,
      contactedVia: row.CONTACTED_VIA,
      hasCalled: row.HAS_CALLED,
      email: row.EMAIL_ID,
      phone: row.PHONE,
    });
  };

  const toggleChannel = (ch) => {
    setContactVia(prev => prev.includes(ch) ? prev.filter(c => c !== ch) : [...prev, ch]);
  };

  const saveContact = async () => {
    if (!contactModal) return;
    setContactSaving(true);
    try {
      await api.companies.markContacted(contactModal.cin, contactNote, contactVia, hasCalled);
      setContactModal(null);
      loadData();
    } catch (e) {
      alert('Error: ' + e.message);
    } finally {
      setContactSaving(false);
    }
  };

  const unmarkContact = async (cin) => {
    if (!window.confirm('Remove contact record for this company?')) return;
    try {
      await api.companies.unmarkContacted(cin);
      loadData();
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  const totalPages = Math.ceil(total / limit);
  const fmt = n => (n == null ? '—' : Number(n).toLocaleString('en-IN'));

  // Count contacted in current visible records (viewer local count fallback)
  const localContactedCount = records.filter(r => r.CONTACTED === 'YES').length;
  const displayContactedCount = contactedCount || localContactedCount;

  // Active filter count badge
  const activeFilterCount = [
    contactedFilter, hasMaster, mgt7aStatus, hasGst, hasIec, contactInfo,
    cityFilter, stateFilter, dateFilters.fromDate, dateFilters.toDate,
    dataSource, intersectionFilter
  ].filter(Boolean).length;

  return (
    <div className="page" onContextMenu={e => e.preventDefault()}>

      {/* ── Header ──────────────────────────────────────────── */}
      <div className="page-header" style={{ marginBottom: '20px' }}>
        <div>
          <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            Companies
            {activeFilterCount > 0 && (
              <span style={{
                fontSize: '11px', fontWeight: 700,
                background: 'var(--navy)', color: '#fff',
                borderRadius: '20px', padding: '2px 10px',
                letterSpacing: '0.04em',
              }}>
                {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''} active
              </span>
            )}
          </h1>
          <p className="page-desc">
            {isViewer
              ? `Viewing batch of ${Math.min(VIEWER_LIMIT, total)} · Contact companies to unlock more`
              : 'Full company data with enrichment status, GST, and IEC flags'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {activeFilterCount > 0 && (
            <button
              onClick={() => {
                setContactedFilter(''); setHasMaster(''); setMgt7aStatus('');
                setHasGst(''); setHasIec(''); setContactInfo('');
                setCityFilter(''); setStateFilter('');
                setDateFilters({ fromDate: '', toDate: '' });
                setDataSource(''); setIntersectionFilter('');
                setPage(1);
              }}
              style={{
                fontSize: '12px', padding: '6px 14px', borderRadius: '20px',
                border: '1.5px solid var(--border)', background: 'var(--surface)',
                color: 'var(--text-2)', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '5px',
                fontWeight: 600, transition: 'all 0.15s',
              }}
            >
              ✕ Clear All Filters
            </button>
          )}
          {!isViewer && (
            <div className="page-badge page-badge--analyst">Analyst View</div>
          )}
        </div>
      </div>

      {/* ── Viewer Progress Banner ───────────────────────────── */}
      {isViewer && (
        <ViewerProgressBanner
          total={total}
          contactedCount={displayContactedCount}
          limit={VIEWER_LIMIT}
        />
      )}

      {/* ── Filter Section ───────────────────────────────────── */}
      <div style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: '14px',
        padding: '18px 20px',
        marginBottom: '14px',
        boxShadow: '0 1px 8px rgba(11,44,74,0.05)',
      }}>
        {/* Search + Contact status pills */}
        <div className="filter-row filter-row--spread" style={{ flexWrap: 'wrap', gap: '10px', marginBottom: '14px' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: '220px', maxWidth: '400px' }}>
            <span style={{
              position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)',
              fontSize: '14px', color: 'var(--text-3)', pointerEvents: 'none',
            }}>🔍</span>
            <input
              className="search-input"
              placeholder="Search by CIN or company name…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ paddingLeft: '34px', width: '100%' }}
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                style={{
                  position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer', fontSize: '14px',
                }}
              >✕</button>
            )}
          </div>
          <div className="filter-pills-inline">
            <button className={`filter-pill-sm ${!contactedFilter ? 'active' : ''}`} onClick={() => { setContactedFilter(''); setPage(1); }}>All</button>
            <button className={`filter-pill-sm ${contactedFilter === 'yes' ? 'active' : ''}`} onClick={() => { setContactedFilter('yes'); setPage(1); }}>✅ Contacted</button>
            <button className={`filter-pill-sm ${contactedFilter === 'no' ? 'active' : ''}`} onClick={() => { setContactedFilter('no'); setPage(1); }}>⬜ Not Yet</button>
          </div>
        </div>

        {/* Data Source Filter Tabs */}
        <div style={{ marginBottom: '12px' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: '8px' }}>
            📂 Data Source
          </div>
          <DataSourceTabs value={dataSource} onChange={v => { setDataSource(v); setIntersectionFilter(''); setPage(1); }} />
        </div>

        {/* Intersection Filters */}
        <div style={{ marginBottom: '14px', paddingTop: '12px', borderTop: '1px solid var(--border)' }}>
          <IntersectionPills value={intersectionFilter} onChange={v => { setIntersectionFilter(v); setDataSource(''); setPage(1); }} />
        </div>

        {/* Row 3: Advanced filters */}
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', paddingTop: '12px', borderTop: '1px solid var(--border)' }}>
          <ColFilter label="City" options={[...new Set(records.map(r => r.CITY).filter(Boolean))].sort().map(v => ({ value: v, label: v }))} value={cityFilter} onChange={v => { setCityFilter(v); setPage(1); }} width={150} />
          <ColFilter label="State" options={[...new Set(records.map(r => r.STATE_ADDR).filter(Boolean))].sort().map(v => ({ value: v, label: v }))} value={stateFilter} onChange={v => { setStateFilter(v); setPage(1); }} width={150} />
          <DateRangeFilter filters={dateFilters} onChange={f => { setDateFilters(f); setPage(1); }} label="REG DATE" />

          {!isViewer && (
            <>
              {[
                { label: 'Master Data', val: hasMaster, set: setHasMaster, opts: [['','All'],['YES','Yes'],['NO','No']] },
                { label: 'MGT-7A', val: mgt7aStatus, set: setMgt7aStatus, opts: [['','All'],['NONE','No Data'],['SUCCESS','Success'],['FAILED: INC-20A REQUIRED','INC-20A Req.'],['FAILED','Failed'],['SESSION_DEAD','Session Dead']] },
                { label: 'GST', val: hasGst, set: setHasGst, opts: [['','All'],['YES','Yes'],['NO','No']] },
                { label: 'IEC', val: hasIec, set: setHasIec, opts: [['','All'],['YES','Yes'],['NO','No']] },
              ].map(({ label, val, set, opts }) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap', fontSize: '12px', fontWeight: 600 }}>{label}</span>
                  <select value={val} onChange={e => { set(e.target.value); setPage(1); }} className="form-select" style={{ padding: '0.2rem 1.2rem 0.2rem 0.4rem', border: '1px solid var(--border)' }}>
                    {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
              ))}
            </>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap', fontSize: '12px', fontWeight: 600 }}>📲 Contact Info</span>
            <select value={contactInfo} onChange={e => { setContactInfo(e.target.value); setPage(1); }} className="form-select" style={{ padding: '0.2rem 1.2rem 0.2rem 0.4rem', border: '1px solid var(--border)' }}>
              <option value="">All</option>
              <option value="email">Has Email</option>
              <option value="phone">Has Phone</option>
              <option value="both">Both</option>
              <option value="either">Either</option>
            </select>
          </div>
        </div>
      </div>

      <ExportBar onExport={handleExport} total={total} canExport={can('export')} />

      {/* ── Table ───────────────────────────────────────────── */}
      <div className="table-card" style={{ marginTop: '12px', borderRadius: '14px', overflow: 'hidden', border: '1px solid var(--border)' }}>
        {loading ? (
          <div className="table-loading" style={{ padding: '60px 0' }}>
            <div style={{ textAlign: 'center' }}>
              <div className="spinner" style={{ marginBottom: '12px' }} />
              <div style={{ fontSize: '13px', color: 'var(--text-3)' }}>Loading companies…</div>
            </div>
          </div>
        ) : (
          <>
            {/* Table meta bar */}
            <div style={{
              padding: '12px 20px',
              borderBottom: '1px solid var(--border)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexWrap: 'wrap', gap: '8px',
              background: 'rgba(244,247,251,0.6)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--navy)' }}>
                  {fmt(total)} companies
                </span>
                {isViewer && (
                  <span style={{
                    fontSize: '11px', padding: '2px 9px', borderRadius: '12px',
                    background: 'rgba(232,197,71,0.12)', color: '#B8960E',
                    border: '1px solid rgba(232,197,71,0.35)', fontWeight: 600,
                  }}>
                    Showing batch {Math.floor(displayContactedCount / VIEWER_LIMIT) + 1}
                  </span>
                )}
                {records.length > 0 && (
                  <span style={{ fontSize: '11px', color: 'var(--text-3)' }}>
                    · {records.filter(r => r.CONTACTED === 'YES').length} contacted on this page
                  </span>
                )}
              </div>
              <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                Page {page} of {totalPages || 1}
              </span>
            </div>

            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th style={{ width: '36px', textAlign: 'center' }}>#</th>
                    <th>CIN</th>
                    <th>Company Name</th>
                    <th>
                      <ColFilter label="City" options={[...new Set(records.map(r => r.CITY).filter(Boolean))].sort().map(v => ({ value: v, label: v }))} value={cityFilter} onChange={v => { setCityFilter(v); setPage(1); }} width={140} />
                    </th>
                    <th>
                      <ColFilter label="State" options={[...new Set(records.map(r => r.STATE_ADDR).filter(Boolean))].sort().map(v => ({ value: v, label: v }))} value={stateFilter} onChange={v => { setStateFilter(v); setPage(1); }} width={140} />
                    </th>
                    <th>Email</th>
                    <th>Phone</th>
                    {!isViewer && <th>INC-20A</th>}
                    {!isViewer && <th>PAN</th>}
                    {!isViewer && <th style={{ textAlign: 'center' }}>GST</th>}
                    {!isViewer && <th style={{ textAlign: 'center' }}>IEC</th>}
                    <th>Contact Status</th>
                    <th style={{ textAlign: 'center' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {records.length === 0 ? (
                    <tr>
                      <td colSpan={isViewer ? 9 : 13} style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--text-3)' }}>
                        <div style={{ fontSize: '32px', marginBottom: '10px' }}>🔍</div>
                        <div style={{ fontWeight: 600, marginBottom: '4px' }}>No companies found</div>
                        <div style={{ fontSize: '12px' }}>Try adjusting your filters</div>
                      </td>
                    </tr>
                  ) : records.map((r, idx) => (
                    <tr key={r.CIN} style={{
                      background: r.CONTACTED === 'YES' ? 'rgba(68,187,164,0.03)' : undefined,
                    }}>
                      <td style={{ textAlign: 'center', color: 'var(--text-3)', fontSize: '11px', fontWeight: 500 }}>
                        {(page - 1) * limit + idx + 1}
                      </td>
                      <td
                        className="mono link-cell"
                        onClick={() => !isViewer && can('mgt7a') && nav(`/companies/${r.CIN}`)}
                        title={!isViewer ? 'View full details' : undefined}
                        style={{ whiteSpace: 'nowrap', fontSize: '11px', letterSpacing: '0.01em' }}
                      >
                        {r.CIN}
                      </td>
                      <td
                        className={!isViewer && can('mgt7a') ? 'link-cell' : ''}
                        onClick={() => !isViewer && can('mgt7a') && nav(`/companies/${r.CIN}`)}
                        style={{ fontWeight: 500 }}
                      >
                        {r.COMPANY_NAME || '—'}
                      </td>
                      <td>{r.CITY || '—'}</td>
                      <td>{r.STATE_ADDR || '—'}</td>

                      {/* Email */}
                      <td className="small-text" style={{ minWidth: '140px', maxWidth: '180px' }}>
                        {r.EMAIL_ID ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              fontSize: '11.5px', color: 'var(--text-2)', flex: 1, minWidth: 0
                            }} title={r.EMAIL_ID}>{r.EMAIL_ID}</span>
                            <a
                              href={`mailto:${r.EMAIL_ID}`}
                              title={`Email: ${r.EMAIL_ID}`}
                              onClick={e => e.stopPropagation()}
                              style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                width: '26px', height: '26px', borderRadius: '6px', flexShrink: 0,
                                background: '#4285F412', border: '1px solid #4285F430',
                                textDecoration: 'none', transition: 'all 0.15s'
                              }}
                            >
                              <img src="https://upload.wikimedia.org/wikipedia/commons/7/7e/Gmail_icon_%282020%29.svg" alt="Email" style={{ width: '14px', height: '14px' }} />
                            </a>
                          </div>
                        ) : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                      </td>

                      {/* Phone */}
                      <td style={{ minWidth: '130px', maxWidth: '165px' }}>
                        {r.PHONE ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{
                              fontSize: '11.5px', color: 'var(--text-2)',
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1
                            }}>{r.PHONE}</span>
                            <a
                              href={`https://wa.me/${r.PHONE.replace(/\D/g, '')}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={`WhatsApp: ${r.PHONE}`}
                              onClick={e => e.stopPropagation()}
                              style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                width: '26px', height: '26px', borderRadius: '6px', flexShrink: 0,
                                background: '#25D36612', border: '1px solid #25D36630',
                                textDecoration: 'none', transition: 'all 0.15s'
                              }}
                            >
                              <img src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg" alt="WhatsApp" style={{ width: '14px', height: '14px' }} />
                            </a>
                          </div>
                        ) : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                      </td>

                      {/* Analyst-only columns */}
                      {!isViewer && (
                        <td>
                          {r.INC_20A_STATUS ? (
                            <span className="status-badge" style={{
                              background: (STATUS_COLORS[r.INC_20A_STATUS] || '#888') + '22',
                              color: STATUS_COLORS[r.INC_20A_STATUS] || '#888',
                              border: `1px solid ${STATUS_COLORS[r.INC_20A_STATUS] || '#888'}`
                            }}>{r.INC_20A_STATUS}</span>
                          ) : '—'}
                        </td>
                      )}
                      {!isViewer && <td className="mono" style={{ fontSize: '11px' }}>{r.PAN || '—'}</td>}
                      {!isViewer && (
                        <td style={{ textAlign: 'center' }}>
                          <span className={r.HAS_GST === 'YES' ? 'badge-yes' : 'badge-no'}>
                            {r.HAS_GST === 'YES' ? '✓' : '✗'}
                          </span>
                        </td>
                      )}
                      {!isViewer && (
                        <td style={{ textAlign: 'center' }}>
                          <span className={r.HAS_IEC === 'YES' ? 'badge-yes' : 'badge-no'}>
                            {r.HAS_IEC === 'YES' ? '✓' : '✗'}
                          </span>
                        </td>
                      )}

                      {/* Contact Status */}
                      <td style={{ minWidth: '140px' }}>
                        {r.CONTACTED === 'YES' ? (
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                              <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#44BBA4', display: 'inline-block', flexShrink: 0 }} />
                              <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#44BBA4' }}>Contacted</span>
                            </div>
                            {r.CONTACTED_BY && (
                              <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>
                                by {r.CONTACTED_BY.split('@')[0]}
                              </div>
                            )}
                            <ContactChannelBadges via={r.CONTACTED_VIA} called={r.HAS_CALLED} />
                            {r.NOTE && (
                              <div style={{
                                fontSize: '10px', color: 'var(--text-2)', marginTop: '3px',
                                maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                              }} title={r.NOTE}>
                                💬 {r.NOTE}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: 'var(--border)', display: 'inline-block' }} />
                            Not yet
                          </span>
                        )}
                      </td>

                      {/* Action */}
                      <td>
                        <div className="action-cell">
                          {r.CONTACTED === 'YES' ? (
                            <>
                              <button className="btn-action btn-action--edit" onClick={() => openContactModal(r)}>✏ Update</button>
                              <button className="btn-action btn-action--danger" onClick={() => unmarkContact(r.CIN)}>✕</button>
                            </>
                          ) : (
                            <button className="btn-action btn-action--contact" onClick={() => openContactModal(r)}>+ Contact</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="pagination" style={{ borderTop: '1px solid var(--border)', padding: '14px 20px' }}>
              <button disabled={page === 1} onClick={() => setPage(1)} className="page-btn" title="First page">«</button>
              <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="page-btn">← Prev</button>
              <div style={{ display: 'flex', gap: '4px' }}>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let p;
                  if (totalPages <= 5) p = i + 1;
                  else if (page <= 3) p = i + 1;
                  else if (page >= totalPages - 2) p = totalPages - 4 + i;
                  else p = page - 2 + i;
                  return (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className="page-btn"
                      style={{
                        background: p === page ? 'var(--navy)' : undefined,
                        color: p === page ? '#fff' : undefined,
                        borderColor: p === page ? 'var(--navy)' : undefined,
                        fontWeight: p === page ? 700 : undefined,
                        minWidth: '36px',
                      }}
                    >
                      {p}
                    </button>
                  );
                })}
              </div>
              <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="page-btn">Next →</button>
              <button disabled={page >= totalPages} onClick={() => setPage(totalPages)} className="page-btn" title="Last page">»</button>
              <span className="page-info" style={{ marginLeft: '8px' }}>
                Page {page} / {totalPages || 1}
              </span>
            </div>
          </>
        )}
      </div>

      {/* ── Contact Modal ────────────────────────────────────── */}
      {contactModal && (
        <div className="modal-overlay" onClick={() => setContactModal(null)}>
          <div
            className="modal"
            style={{ maxWidth: '520px', width: '92vw' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="modal-header" style={{
              background: 'linear-gradient(135deg, var(--navy) 0%, var(--navy-3) 100%)',
              borderRadius: 'var(--radius) var(--radius) 0 0',
              padding: '20px 24px',
              display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px'
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <span style={{
                    width: '8px', height: '8px', borderRadius: '50%',
                    background: contactModal.contacted ? '#44BBA4' : '#E8C547',
                    display: 'inline-block', flexShrink: 0,
                    boxShadow: `0 0 0 3px ${contactModal.contacted ? '#44BBA422' : '#E8C54722'}`
                  }} />
                  <h2 style={{ fontSize: '15px', fontWeight: 700, color: '#fff', margin: 0 }}>
                    {contactModal.contacted ? 'Update Contact Record' : 'Mark as Contacted'}
                  </h2>
                </div>
                <p style={{ fontSize: '13px', fontWeight: 600, color: 'rgba(255,255,255,0.9)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {contactModal.name}
                </p>
                <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.45)', margin: '2px 0 0', fontFamily: 'DM Mono, monospace' }}>
                  {contactModal.cin}
                </p>
              </div>
              <button className="modal-close" onClick={() => setContactModal(null)} style={{
                background: 'rgba(255,255,255,0.1)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', flexShrink: 0
              }}>✕</button>
            </div>

            {/* Modal Body */}
            <div className="modal-body" style={{ padding: '24px', gap: '20px', display: 'flex', flexDirection: 'column' }}>

              {/* Contact details */}
              {(contactModal.email || contactModal.phone) && (
                <div style={{
                  display: 'flex', gap: '10px', flexWrap: 'wrap',
                  background: 'var(--bg)', borderRadius: '8px', padding: '12px 14px',
                  border: '1px solid var(--border)'
                }}>
                  {contactModal.email && (
                    <a href={`mailto:${contactModal.email}`} style={{
                      display: 'flex', alignItems: 'center', gap: '6px',
                      fontSize: '12px', color: '#4285F4', fontWeight: 500,
                      background: '#4285F410', padding: '5px 10px', borderRadius: '6px',
                      border: '1px solid #4285F430', textDecoration: 'none'
                    }}>✉️ {contactModal.email}</a>
                  )}
                  {contactModal.phone && (
                    <a href={`https://wa.me/${contactModal.phone.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" style={{
                      display: 'flex', alignItems: 'center', gap: '6px',
                      fontSize: '12px', color: '#25D366', fontWeight: 500,
                      background: '#25D36610', padding: '5px 10px', borderRadius: '6px',
                      border: '1px solid #25D36630', textDecoration: 'none'
                    }}>💬 {contactModal.phone}</a>
                  )}
                </div>
              )}

              {/* How was contact made */}
              <div>
                <label style={{
                  display: 'block', fontSize: '11px', fontWeight: 700,
                  letterSpacing: '0.07em', textTransform: 'uppercase',
                  color: 'var(--text-2)', marginBottom: '10px'
                }}>How was contact made?</label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {CONTACT_CHANNELS.map(ch => {
                    const active = contactVia.includes(ch.id);
                    return (
                      <button
                        key={ch.id}
                        onClick={() => toggleChannel(ch.id)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '7px',
                          padding: '8px 14px', borderRadius: '8px', cursor: 'pointer',
                          fontSize: '13px', fontWeight: 600,
                          border: `2px solid ${active ? ch.color : 'var(--border)'}`,
                          background: active ? ch.color + '15' : 'var(--surface)',
                          color: active ? ch.color : 'var(--text-2)',
                          transition: 'all 0.15s ease', outline: 'none',
                        }}
                      >
                        <span style={{ fontSize: '16px' }}>{ch.icon}</span>
                        {ch.label}
                        {active && <span style={{
                          width: '16px', height: '16px', borderRadius: '50%',
                          background: ch.color, color: '#fff',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '10px', fontWeight: 800, marginLeft: '2px'
                        }}>✓</span>}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Previous record */}
              {contactModal.contacted && (contactModal.contactedBy || contactModal.contactedVia) && (
                <div style={{
                  background: 'rgba(68,187,164,0.06)',
                  border: '1px solid rgba(68,187,164,0.25)',
                  borderRadius: '8px', padding: '12px 14px',
                  fontSize: '12px', color: 'var(--text-2)'
                }}>
                  <div style={{ fontWeight: 700, color: '#44BBA4', marginBottom: '6px', fontSize: '11px', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                    Previous Record
                  </div>
                  {contactModal.contactedBy && (
                    <div style={{ marginBottom: '3px' }}>
                      👤 Contacted by <strong>{contactModal.contactedBy.split('@')[0]}</strong>
                      {contactModal.contactedBy.includes('@') && (
                        <span style={{ color: 'var(--text-muted)' }}> ({contactModal.contactedBy})</span>
                      )}
                    </div>
                  )}
                  {contactModal.contactedVia && (
                    <div style={{ marginTop: '4px' }}>
                      <ContactChannelBadges via={contactModal.contactedVia} called={contactModal.hasCalled} />
                    </div>
                  )}
                  {contactModal.note && (
                    <div style={{
                      marginTop: '8px', padding: '8px 10px',
                      background: 'rgba(255,255,255,0.6)', borderRadius: '6px',
                      borderLeft: '3px solid #44BBA4', color: 'var(--text)', fontStyle: 'italic'
                    }}>
                      "{contactModal.note}"
                    </div>
                  )}
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="form-label" style={{
                  display: 'block', fontSize: '11px', fontWeight: 700,
                  letterSpacing: '0.07em', textTransform: 'uppercase',
                  color: 'var(--text-2)', marginBottom: '8px'
                }}>Comments / Notes</label>
                <textarea
                  className="form-textarea"
                  rows={3}
                  placeholder="What was discussed? Any follow-up needed? Key points from conversation…"
                  value={contactNote}
                  onChange={e => setContactNote(e.target.value)}
                  style={{ width: '100%', resize: 'vertical', borderColor: contactNote ? 'var(--navy-3)' : 'var(--border)', fontSize: '13px', lineHeight: 1.6 }}
                />
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px', textAlign: 'right' }}>
                  {contactNote.length}/500
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', paddingTop: '4px', borderTop: '1px solid var(--border)' }}>
                <button className="btn-secondary" onClick={() => setContactModal(null)}>Cancel</button>
                <button
                  className="btn-primary"
                  onClick={saveContact}
                  disabled={contactSaving || contactVia.length === 0}
                  style={{
                    opacity: contactVia.length === 0 ? 0.55 : 1,
                    cursor: contactVia.length === 0 ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', gap: '8px'
                  }}
                  title={contactVia.length === 0 ? 'Select at least one contact channel' : ''}
                >
                  {contactSaving ? (
                    <><span className="spinner spinner--sm" /> Saving…</>
                  ) : (
                    <>{contactModal.contacted ? '↺ Update Record' : '✓ Mark as Contacted'}</>
                  )}
                </button>
              </div>

              {contactVia.length === 0 && (
                <p style={{ fontSize: '11px', color: 'var(--red)', textAlign: 'right', marginTop: '-14px' }}>
                  ↑ Please select how contact was made
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
